import { Plane, Heart, MapPin, Route, Briefcase, Clock } from "lucide-react";

const SERVICES = [
  {
    icon: Plane,
    title: "Transferts Aéroport & Gare",
    description:
      "Brest, Rennes, Nantes... Prise en charge ponctuelle avec suivi des vols et trains en temps réel.",
    image: "https://images.unsplash.com/photo-1761377197584-2eed555e2b0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGV4ZWN1dGl2ZSUyMHRyYXZlbGVyJTIwYWlycG9ydCUyMGxvdW5nZXxlbnwwfHx8fDE3NzM3NDI5NTJ8MA&ixlib=rb-4.1.0&q=85&w=600",
    accent: true,
  },
  {
    icon: Heart,
    title: "Mariages & Événements",
    description:
      "Un service irréprochable pour le plus beau jour de votre vie. Décoration florale sur demande.",
    image: "https://images.unsplash.com/photo-1766416143535-d9cfcad729eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxicmlkZSUyMGdyb29tJTIwZWxlZ2FudCUyMGJsYWNrJTIwd2hpdGV8ZW58MHx8fHwxNzczNzQyOTYyfDA&ixlib=rb-4.1.0&q=85&w=600",
    accent: true,
  },
  {
    icon: MapPin,
    title: "Circuits Touristiques",
    description:
      "Découvrez la Bretagne autrement : côtes sauvages, phares mythiques, gastronomie locale.",
    image: "https://images.unsplash.com/photo-1697310393906-575a67adda20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxsaWdodGhvdXNlJTIwb2NlYW4lMjBzdW5zZXR8ZW58MHx8fHwxNzczNzQyOTYzfDA&ixlib=rb-4.1.0&q=85&w=600",
    accent: true,
  },
  {
    icon: Route,
    title: "Longue Distance",
    description:
      "Jusqu'à 500 km autour de Brest. Profitez du confort premium de notre van électrique, même sur vos trajets les plus longs.",
  },
  {
    icon: Briefcase,
    title: "Déplacements Professionnels",
    description:
      "Séminaires, rendez-vous d'affaires, mise à disposition. Discrétion et ponctualité absolues.",
  },
  {
    icon: Clock,
    title: "Mise à Disposition",
    description:
      "À l'heure, à la demi-journée ou à la journée. Votre chauffeur reste à votre entière disposition.",
  },
];

export default function ServicesSection() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-28 lg:py-40 bg-[#131313]"
    >
      {/* Subtle radial glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gold/[0.02] rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className="mb-24 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div>
            <p className="font-accent italic text-gold text-base tracking-widest mb-4">
              Nos prestations
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight">
              Services
            </h2>
          </div>
          <div className="hidden lg:block">
            <div className="gold-line w-32" />
            <p className="text-white/40 text-sm mt-4 max-w-xs font-light tracking-wide leading-relaxed text-right">
              Un service sur-mesure pour chaque occasion, chaque trajet, chaque client.
            </p>
          </div>
        </div>

        {/* Top row - 3 large cards with images */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
          {SERVICES.slice(0, 3).map((service, idx) => {
            const Icon = service.icon;
            return (
              <div
                key={idx}
                data-testid={`service-card-${idx}`}
                className="service-card group relative overflow-hidden bg-[#1A1A1A] min-h-[420px] flex flex-col justify-end"
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover opacity-30 group-hover:opacity-40 group-hover:scale-110 transition-all duration-1000"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/60 to-transparent" />
                </div>

                {/* Corner accent */}
                <div className="absolute top-0 right-0 w-12 h-12 border-r border-t border-gold/0 group-hover:border-gold/30 transition-all duration-700" />

                {/* Content */}
                <div className="relative z-10 p-8 lg:p-10">
                  <Icon
                    size={24}
                    className="service-icon text-gold/60 mb-5 transition-colors duration-500"
                    strokeWidth={1.2}
                  />
                  <h3 className="service-title font-display text-xl lg:text-2xl text-white mb-3 transition-colors duration-500">
                    {service.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed font-light tracking-wide">
                    {service.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom row - 3 smaller cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {SERVICES.slice(3).map((service, idx) => {
            const Icon = service.icon;
            return (
              <div
                key={idx + 3}
                data-testid={`service-card-${idx + 3}`}
                className="service-card group relative overflow-hidden bg-[#1A1A1A] p-8 lg:p-10 min-h-[220px] flex flex-col justify-end"
              >
                {/* Corner accent */}
                <div className="absolute top-0 right-0 w-10 h-10 border-r border-t border-gold/0 group-hover:border-gold/20 transition-all duration-700" />

                <div className="relative z-10">
                  <Icon
                    size={22}
                    className="service-icon text-white/25 mb-5 transition-colors duration-500"
                    strokeWidth={1.2}
                  />
                  <h3 className="service-title font-display text-lg text-white mb-3 transition-colors duration-500">
                    {service.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed font-light tracking-wide">
                    {service.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
